/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps } from 'preact';
import { useReducer } from 'preact/hooks';
import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { DateField } from './DateField';
import type { DateGranularity, DatePlaceholders } from './types';
import { dateReducer } from './dateReducer';
import { getDateFieldInitialReducerState } from './getDateFieldInitialReducerState';

type DateFieldType = ComponentProps<typeof DateField>;

type UseDateFieldStateProps = {
  dateMasks: DatePlaceholders;
  granularity: DateGranularity;
  locale: BCP47Locale;
  value: DateFieldType['value'];
};

type DateFieldInitialStateProps = UseDateFieldStateProps;

/**
 * Hook that initializes the date state by calling useReducer.
 * It returns a state and a dispatch method to request changes to that state.
 */
const useDateFieldState = ({
  dateMasks,
  granularity,
  locale,
  value: currentValue
}: UseDateFieldStateProps) => {
  const init = (props: DateFieldInitialStateProps) => getDateFieldInitialReducerState(props);

  const [state, dispatch] = useReducer(
    dateReducer,
    {
      dateMasks,
      granularity,
      locale,
      value: currentValue
    },
    init
  );

  return { dispatch, state };
};

export { useDateFieldState };
